import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import {
    Info,
    Shield,
    EnvelopeSimple,
    ArrowSquareOut,
    HandsPraying,
    WarningOctagon,
    Trash,
    Translate,
    Cookie,
    SlidersHorizontal,
} from "@phosphor-icons/react";
import BiometricSection from "@/components/settings/BiometricSection";
import DeleteAccountModal from "@/components/settings/DeleteAccountModal";
import { toast } from "sonner";
import {
    ES_EMPHASIS, EN_EMPHASIS,
    decorateParagraph, linkifySources,
} from "@/lib/settingsText";

const PRIVACY_POLICY_URL = "/privacy-policy.html";
const COOKIE_POLICY_URL  = "/cookie-policy.html";
const SUPPORT_EMAIL      = "info@corazonesalaobra.org";
const APP_VERSION        = "1.0.0";
const APP_DOMAIN         = "soyapostol.org";
const APP_LOCATION       = "Houston, TX, US";

export default function Settings() {
    const { t, lang, setLang } = useLang();
    const { user, deleteAccount } = useAuth();
    const navigate = useNavigate();

    const emphasis = lang === "es" ? ES_EMPHASIS : EN_EMPHASIS;
    const paragraphs = [
        t("settings.about.p1"),
        t("settings.about.p2"),
        t("settings.about.p3_heading"),   // rendered as subheading below
        t("settings.about.p4"),
    ];

    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const onContactSupport = () => {
        const subject = encodeURIComponent(t("settings.support.subject"));
        const body    = encodeURIComponent(t("settings.support.body"));
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    };

    const onOpenPrivacy = () => {
        window.open(PRIVACY_POLICY_URL, "_blank", "noopener,noreferrer");
    };
    const onOpenCookiePolicy = () => {
        window.open(COOKIE_POLICY_URL, "_blank", "noopener,noreferrer");
    };
    // Termly's resource-blocker exposes `displayPreferenceModal` globally
    // once it has loaded. We guard for the case where the script is
    // blocked (ad-blockers, offline) and surface a friendly toast so the
    // button never silently does nothing.
    const onOpenCookiePreferences = () => {
        if (typeof window.displayPreferenceModal === "function") {
            window.displayPreferenceModal();
            return;
        }
        toast.error(
            lang === "es"
                ? "El gestor de cookies aún no está disponible. Recarga la página."
                : "The cookie manager isn't ready yet. Please reload the page.",
        );
    };

    // Modal-supplied callback. Returns true on success so the modal can
    // keep its spinner while we navigate; false means we leave the modal
    // open with an error message.
    const onConfirmDelete = async (email) => {
        const ok = await deleteAccount(email, lang);
        if (!ok) return false;
        try {
            ["soyapostol:examen:es", "soyapostol:examen:en"].forEach(
                (k) => localStorage.removeItem(k),
            );
        } catch { /* ignore */ }
        navigate("/account-deleted", { replace: true });
        return true;
    };

    return (
        <div data-testid="settings-page" className="max-w-[720px] mx-auto">
            <p className="label-eyebrow mb-3">{t("settings.eyebrow")}</p>
            <h1 className="heading-serif text-4xl sm:text-5xl tracking-tight leading-none mb-3">
                {t("settings.title")}
            </h1>
            <p className="text-stoneMuted mb-10">{t("settings.subtitle")}</p>

            {/* ============================================================ */}
            {/* Idioma — Preferencia de usuario                              */}
            {/* ============================================================ */}
            <section className="mb-12" data-testid="settings-language">
                <header className="flex items-center gap-3 mb-5">
                    <span
                        className="shrink-0 w-10 h-10 rounded-full bg-sand-200 text-stoneMuted flex items-center justify-center"
                        aria-hidden="true"
                    >
                        <Translate size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="label-eyebrow">{t("settings.language.eyebrow")}</p>
                        <h2 className="heading-serif text-2xl sm:text-3xl tracking-tight m-0">
                            {t("settings.language.title")}
                        </h2>
                    </div>
                </header>

                <article className="surface-card p-5 sm:p-6" data-testid="settings-language-card">
                    <p className="ui-sans text-sm leading-relaxed text-stoneMuted mb-4">
                        {t("settings.language.subtitle")}
                    </p>
                    <div
                        role="radiogroup"
                        aria-label={t("settings.language.title")}
                        className="grid grid-cols-2 gap-2"
                        data-testid="settings-language-toggle"
                    >
                        <button
                            type="button"
                            role="radio"
                            aria-checked={lang === "es"}
                            onClick={() => setLang("es")}
                            data-testid="settings-language-es"
                            className={`px-4 py-3 rounded-md ui-sans text-sm font-semibold transition-colors border ${
                                lang === "es"
                                    ? "bg-sangre text-sand-50 border-sangre"
                                    : "bg-sand-50 text-stone900 border-sand-300 hover:border-sangre"
                            }`}
                        >
                            <span className="block text-xs uppercase tracking-widest mb-0.5 opacity-80">ES</span>
                            <span className="block">{t("settings.language.spanish")}</span>
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={lang === "en"}
                            onClick={() => setLang("en")}
                            data-testid="settings-language-en"
                            className={`px-4 py-3 rounded-md ui-sans text-sm font-semibold transition-colors border ${
                                lang === "en"
                                    ? "bg-sangre text-sand-50 border-sangre"
                                    : "bg-sand-50 text-stone900 border-sand-300 hover:border-sangre"
                            }`}
                        >
                            <span className="block text-xs uppercase tracking-widest mb-0.5 opacity-80">EN</span>
                            <span className="block">{t("settings.language.english")}</span>
                        </button>
                    </div>
                    {!(user && user.email) && (
                        <p
                            className="ui-sans text-xs leading-relaxed text-stoneFaint mt-4 m-0"
                            data-testid="settings-language-anonymous-hint"
                        >
                            {t("settings.language.anonymous_hint")}
                        </p>
                    )}
                </article>
            </section>

            {/* ============================================================ */}
            {/* Biometría — Face ID / Touch ID                               */}
            {/* ============================================================ */}
            {user && user.email && <BiometricSection lang={lang} t={t} />}


            {/* ============================================================ */}
            {/* Sobre la App — Quiénes Somos                                 */}
            {/* ============================================================ */}
            <section className="mb-12" data-testid="settings-about">
                <header className="flex items-center gap-3 mb-5">
                    <span
                        className="shrink-0 w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center"
                        aria-hidden="true"
                    >
                        <Info size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="label-eyebrow text-purple-700">
                            {t("settings.about.eyebrow")}
                        </p>
                        <h2 className="heading-serif text-2xl sm:text-3xl tracking-tight m-0">
                            {t("settings.about.title")}
                        </h2>
                    </div>
                </header>

                <article className="surface-card p-6 sm:p-8">
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 mb-5"
                        data-testid="settings-about-p1"
                    >
                        {decorateParagraph(paragraphs[0], emphasis)}
                    </p>
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 mb-8"
                        data-testid="settings-about-p2"
                    >
                        {decorateParagraph(paragraphs[1], emphasis)}
                    </p>

                    <h3
                        className="heading-serif text-xl tracking-tight mb-3 flex items-center gap-2"
                        data-testid="settings-about-domain-heading"
                    >
                        <HandsPraying size={18} weight="duotone" className="text-purple-700" />
                        <span>{decorateParagraph(paragraphs[2], emphasis)}</span>
                    </h3>
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 m-0"
                        data-testid="settings-about-p4"
                    >
                        {decorateParagraph(paragraphs[3], emphasis)}
                    </p>
                </article>

                {/* ----- Créditos / Credits ----- */}
                <article
                    className="surface-card p-6 sm:p-8 mt-6"
                    data-testid="settings-credits"
                >
                    <h3 className="heading-serif text-xl tracking-tight mb-3 flex items-center gap-2">
                        <HandsPraying size={18} weight="duotone" className="text-purple-700" />
                        <span>{t("settings.credits.title")}</span>
                    </h3>
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 mb-4"
                        data-testid="settings-credits-intro"
                    >
                        {linkifySources(t("settings.credits.intro"))}
                    </p>
                    <ul
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 mb-4 list-disc pl-6 space-y-1.5"
                        data-testid="settings-credits-list"
                    >
                        <li>
                            <strong className="font-semibold text-stone900">
                                {t("settings.credits.row_readings_label")}
                            </strong>
                            {" "}
                            {linkifySources(t("settings.credits.row_readings_value"))}
                        </li>
                        <li>
                            <strong className="font-semibold text-stone900">
                                {t("settings.credits.row_liturgy_label")}
                            </strong>
                            {" "}
                            {linkifySources(t("settings.credits.row_liturgy_value"))}
                        </li>
                        <li>
                            <strong className="font-semibold text-stone900">
                                {t("settings.credits.row_news_label")}
                            </strong>
                            {" "}
                            {linkifySources(t("settings.credits.row_news_value"))}
                        </li>
                    </ul>
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stone900 mb-4"
                        data-testid="settings-credits-lev"
                    >
                        {linkifySources(t("settings.credits.lev"))}
                    </p>
                    <p
                        className="reading-serif text-base sm:text-lg leading-[1.85] text-stoneMuted italic m-0"
                        data-testid="settings-credits-disclaimer"
                    >
                        {t("settings.credits.disclaimer")}
                    </p>
                    {user && user.email && (
                        <p
                            className="mt-4 pt-4 border-t border-sand-300 ui-sans text-sm leading-relaxed text-stoneMuted m-0"
                            data-testid="settings-credits-right-to-forget"
                        >
                            <strong className="font-semibold text-stone900">
                                {t("settings.credits.right_to_forget_label")}
                            </strong>
                            {" "}
                            {t("settings.credits.right_to_forget_body")}
                            {" "}
                            <button
                                type="button"
                                onClick={() => setConfirmOpen(true)}
                                data-testid="settings-credits-delete-link"
                                className="text-red-700 hover:text-red-800 underline decoration-red-300 underline-offset-4 transition-colors"
                            >
                                {t("settings.credits.right_to_forget_cta")}
                            </button>
                            .
                        </p>
                    )}
                </article>
            </section>

            {/* ============================================================ */}
            {/* Legal                                                         */}
            {/* ============================================================ */}
            <section className="mb-12" data-testid="settings-legal">
                <header className="flex items-center gap-3 mb-5">
                    <span
                        className="shrink-0 w-10 h-10 rounded-full bg-sand-200 text-stoneMuted flex items-center justify-center"
                        aria-hidden="true"
                    >
                        <Shield size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="label-eyebrow">{t("settings.legal.eyebrow")}</p>
                        <h2 className="heading-serif text-2xl sm:text-3xl tracking-tight m-0">
                            {t("settings.legal.title")}
                        </h2>
                    </div>
                </header>

                <div className="flex flex-col gap-3">
                    <button
                        type="button"
                        onClick={onOpenPrivacy}
                        data-testid="settings-privacy-btn"
                        className="w-full surface-card p-5 text-left flex items-center justify-between gap-4 hover:border-purple-400 transition-colors"
                    >
                        <div className="min-w-0 flex items-start gap-3">
                            <Shield
                                size={22}
                                weight="duotone"
                                className="shrink-0 text-purple-700 mt-0.5"
                                aria-hidden="true"
                            />
                            <div className="min-w-0">
                                <p className="ui-sans font-semibold text-stone900 mb-0.5">
                                    {t("settings.legal.privacy_title")}
                                </p>
                                <p className="text-sm text-stoneMuted m-0">
                                    {t("settings.legal.privacy_hint")}
                                </p>
                            </div>
                        </div>
                        <ArrowSquareOut
                            size={20}
                            weight="duotone"
                            className="shrink-0 text-stoneFaint"
                            aria-hidden="true"
                        />
                    </button>

                    <button
                        type="button"
                        onClick={onOpenCookiePolicy}
                        data-testid="settings-cookies-policy-btn"
                        className="w-full surface-card p-5 text-left flex items-center justify-between gap-4 hover:border-purple-400 transition-colors"
                    >
                        <div className="min-w-0 flex items-start gap-3">
                            <Cookie
                                size={22}
                                weight="duotone"
                                className="shrink-0 text-purple-700 mt-0.5"
                                aria-hidden="true"
                            />
                            <div className="min-w-0">
                                <p className="ui-sans font-semibold text-stone900 mb-0.5">
                                    {t("settings.legal.cookies_title")}
                                </p>
                                <p className="text-sm text-stoneMuted m-0">
                                    {t("settings.legal.cookies_hint")}
                                </p>
                            </div>
                        </div>
                        <ArrowSquareOut
                            size={20}
                            weight="duotone"
                            className="shrink-0 text-stoneFaint"
                            aria-hidden="true"
                        />
                    </button>

                    <button
                        type="button"
                        onClick={onOpenCookiePreferences}
                        data-testid="settings-cookies-preferences-btn"
                        className="w-full surface-card p-5 text-left flex items-center justify-between gap-4 hover:border-purple-400 transition-colors"
                    >
                        <div className="min-w-0 flex items-start gap-3">
                            <SlidersHorizontal
                                size={22}
                                weight="duotone"
                                className="shrink-0 text-purple-700 mt-0.5"
                                aria-hidden="true"
                            />
                            <div className="min-w-0">
                                <p className="ui-sans font-semibold text-stone900 mb-0.5">
                                    {t("settings.legal.cookie_prefs_title")}
                                </p>
                                <p className="text-sm text-stoneMuted m-0">
                                    {t("settings.legal.cookie_prefs_hint")}
                                </p>
                            </div>
                        </div>
                    </button>
                </div>
            </section>

            {/* ============================================================ */}
            {/* Soporte                                                       */}
            {/* ============================================================ */}
            <section className="mb-12" data-testid="settings-support">
                <header className="flex items-center gap-3 mb-5">
                    <span
                        className="shrink-0 w-10 h-10 rounded-full bg-sangre/10 text-sangre flex items-center justify-center"
                        aria-hidden="true"
                    >
                        <EnvelopeSimple size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="label-eyebrow text-sangre">
                            {t("settings.support.eyebrow")}
                        </p>
                        <h2 className="heading-serif text-2xl sm:text-3xl tracking-tight m-0">
                            {t("settings.support.title")}
                        </h2>
                    </div>
                </header>

                <button
                    type="button"
                    onClick={onContactSupport}
                    data-testid="settings-support-btn"
                    className="w-full surface-card p-5 text-left flex items-center justify-between gap-4 hover:border-sangre transition-colors"
                >
                    <div className="min-w-0">
                        <p className="ui-sans font-semibold text-stone900 mb-0.5">
                            {t("settings.support.cta")}
                        </p>
                        <p className="text-sm text-stoneMuted m-0 truncate">
                            {SUPPORT_EMAIL}
                        </p>
                    </div>
                    <EnvelopeSimple
                        size={20}
                        weight="duotone"
                        className="shrink-0 text-sangre"
                        aria-hidden="true"
                    />
                </button>
            </section>

            {/* ============================================================ */}
            {/* Danger zone — account deletion                               */}
            {/* ============================================================ */}
            {user && user.email && (
                <section className="mb-12" data-testid="settings-danger">
                    <header className="flex items-center gap-3 mb-5">
                        <span
                            className="shrink-0 w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center"
                            aria-hidden="true"
                        >
                            <WarningOctagon size={20} weight="duotone" />
                        </span>
                        <div>
                            <p className="label-eyebrow text-red-700">
                                {t("settings.danger.eyebrow")}
                            </p>
                            <h2 className="heading-serif text-2xl sm:text-3xl tracking-tight m-0">
                                {t("settings.danger.title")}
                            </h2>
                        </div>
                    </header>

                    <article
                        className="border border-red-200 bg-red-50/50 rounded-md p-5 sm:p-6"
                        data-testid="settings-danger-card"
                    >
                        <p className="ui-sans text-sm leading-relaxed text-stone900 mb-4">
                            {t("settings.danger.body")}
                        </p>
                        <ul className="ui-sans text-sm text-stone900 list-disc pl-5 space-y-1 mb-5">
                            <li>{t("settings.danger.list_user")}</li>
                            <li>{t("settings.danger.list_favs")}</li>
                            <li>{t("settings.danger.list_sessions")}</li>
                        </ul>
                        <button
                            type="button"
                            onClick={() => setConfirmOpen(true)}
                            data-testid="settings-delete-account-btn"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-700 hover:bg-red-800 text-white ui-sans font-semibold rounded-md transition-colors"
                        >
                            <Trash size={16} weight="bold" />
                            {t("settings.danger.cta")}
                        </button>
                    </article>
                </section>
            )}

            <DeleteAccountModal
                open={confirmOpen && !!(user && user.email)}
                onClose={() => setConfirmOpen(false)}
                onConfirm={onConfirmDelete}
                userEmail={user?.email || ""}
                t={t}
            />

            {/* ============================================================ */}
            {/* Footer                                                        */}
            {/* ============================================================ */}
            <footer
                className="mt-16 pt-8 border-t border-sand-300 text-xs text-stoneFaint text-center"
                data-testid="settings-footer"
            >
                {t("settings.footer.version", { v: APP_VERSION })} · {APP_DOMAIN} · {APP_LOCATION}
            </footer>
        </div>
    );
}
